'use strict';

import * as _ from 'lodash-es';

(function () {
  angular
    .module('specsguardApp')
    .factory('Auth', Auth);

  Auth.$inject = ['$rootScope', '$http', '$cookies', '$q', 'User', '$log', '$window', 'Constants'];

  function Auth ($rootScope, $http, $cookies, $q, User, $log, $window, Constants) {

    let currentUser = angular.isDefined($cookies.getObject('user')) ? $cookies.getObject('user') : {};

    (function cacheUserRoles () {
      return $q(function (resolve) {
        if (_.isEmpty($cookies.getObject('userRoles'))) {
          // get user roles
          $http.get('/api/user/roles')
            .then(function (userRoles) {
              $cookies.putObject('userRoles', userRoles.data);
              resolve(userRoles);
            }, function (err) {
              $log.error('Err retrieving userRoles: ', err);
            });
        } else {
          resolve($cookies.getObject('userRoles'));
        }
      });
    })();

    return {
      /**
       * Authenticate user and save token
       *
       * @param  {Object}   user     - login info
       * @param  {Function} callback - optional
       * @return {Promise}
       */
      login: function (user, callback) {
        const cb = callback || angular.noop;
        let res;

        return $http.post('/api/user/login', {
          email: user.email,
          password: user.password
        })
          .then(function (data) {
            res = data;
            if (res.data.status === 'fail') {
              return Promise.reject(res.data.error);
            }

            $cookies.put('sg', res.data.token);
            return User.getMe().$promise;
          })
          .then((cUser) => {
            currentUser = cUser;
            return Promise.resolve();
          })
          .then(() => {
            $rootScope.$emit('loggedIn', res.data.token);

            // Whenever we log in, invalidate the tenant specific app constants
            // The updated versions will be retrieved on next transition (navigation).
            // Why? when switching tenants we always have correct (tenant specific) constants.
            // We only invalidate constants, however, when there are no constants, also Help, Labels and Menu
            // will get refreshed (see app.run.js)
            Constants.invalidate();

            if (cb) {
              cb();
            }
            return Promise.resolve(res);
          })
          .catch((err) => {
            this.logout();
            if (cb) {
              cb(err);
            }

            return Promise.reject(err);
          });
      },

      /**
       * Delete access token and user info
       *
       */
      logout: function () {
        $cookies.remove('user');
        $cookies.remove('sg');
        currentUser = {};
        $rootScope.$emit('loggedOut');
      },

      /**
       * Create a new user
       *
       * @param  {Object}   user     - user info
       * @param  {Function} [callback] - optional
       * @return {Promise}
       */
      createUser: function (user, callback) {
        const cb = callback || angular.noop;

        return User.save({ user: user },
          function (data) {
            return cb(data);
          },
          function (err) {
            return cb(err);
          }.bind(this)).$promise; // eslint-disable-line
      },

      /**
       * Reactivate user (resends activation email)
       *
       * @param  {String}   userId
       * @param  {Function} [callback] - optional
       * @return {Promise}
       */
      reactivateUser: function (userId, callback) {
        const cb = callback || angular.noop;
        return User.reactivate({ id: userId }, {
        }, function (user) {
          return cb(user);
        }, function (err) {
          return cb(err);
        }).$promise;
      },

      /**
       * Send (password) token so user can reset the user password
       *
       * @param {String} userMail
       * @return {Promise}
       */
      sendPasswordByEmail: function (userEmail) {
        return $q(function (resolve, reject) {
          $http.put('/api/user/mailpassword', { email: userEmail })
            .then(function (data) {
              resolve(data);
            }, function (err) {
              reject(err);
            });
        });
      },

      /**
       * Reactivate user by email account (resends activation email)
       *
       * @param  {String}   userEmail
       * @return {Promise}
       */
      reactivateUserByMail: function (userEmail) {
        return $q(function (resolve, reject) {
          $http.put('/api/user/mailactivation', { email: userEmail })
            .then(function (data) {
              resolve(data);
            }, function (err) {
              reject(err);
            });
        });
      },

      /**
       * Activate user with new password
       *
       * @param  {String}   userId
       * @param  {String}   activationToken
       * @param  {String}   newPassword
       * @param  {Function} callback    - optional
       * @return {Promise}
       */
      activateUser: function (userId, activationToken, newPassword, callback) {
        const cb = callback || angular.noop;
        return User.activate({
          userId: userId,
          activationToken: activationToken,
          password: newPassword
        }, function (user) {
          return cb(user);
        }, function (err) {
          return cb(err);
        }).$promise;
      },

      /**
       * Change password
       *
       * @param  {String}   oldPassword
       * @param  {String}   newPassword
       * @param  {Function} callback    - optional
       * @return {Promise}
       */
      changePassword: function (oldPassword, newPassword, callback) {
        const cb = callback || angular.noop;
        return User.changePassword({
          oldPass: oldPassword,
          newPass: newPassword
        }, function (user) {
          return cb(user);
        }, function (err) {
          return cb(err);
        }).$promise;
      },

      /**
       * Gets all available info on authenticated user
       *
       * @return {User} user
       */
      getCurrentUser: function (): User {
        return currentUser;
      },

      /**
       * Gets the current user as a promise.
       *
       * @returns A promise with the current user.
       */
      getCurrentUserResolver: function(): Promise<User> {
        return new Promise(resolve => {
          setTimeout(() => {
            resolve(currentUser);
          }, 500);
        });
      },

      /**
       * Gets language of authenticated user
       *
       * @return {String} language
       */
      getCurrentUserLanguage: function () {
        return currentUser.userLanguage || 'nl';
      },

      getCurrentUserTenant: function () {
        return currentUser && currentUser.databases[0];
      },

      /**
       * Gets all user role definitions
       *
       * @return {Object} user roles
       */
      getUserRoles: function () {
        return $cookies.getObject('userRoles');
      },

      /**
       * Gets current user role
       *
       * @return {String} user role
       */
      getCurrentUserRole: function () {
        return this.getUserRoles() && this.getUserRoles().find((userRole) => currentUser.role === userRole.value);
      },

      /**
       * Check if a user is guest
       *
       * @return {Boolean}
       */
      isUserGuest: function () {
        return (this.getCurrentUserRole().value === 'guest');
      },

      /**
       * Returns a promise that gets the authenticated user's role access level
       *
       * @return {Promise} which will get the {Number} accessLevel
       */
      getUserAccessLevel: function () {
        const _this = this;
        const userRole = _this.getCurrentUserRole();
        if (!_.isEmpty(currentUser) && !_.isEmpty(userRole)) {
          return _this.getCurrentUserRole().accessLevel;
        }
        $log.info('currentUser: ', currentUser, 'userRole: ', userRole);
        throw new Error("current user is not set or could not get current user's role.");
      },

      /**
       * Check if a user is logged in
       *
       * @return {Boolean}
       */
      isLoggedIn: function () {
        const isLoggedIn = currentUser.hasOwnProperty('role') && !this.tokenExpired();
        return isLoggedIn;
      },

      /**
       * Check if a user is an admin
       *
       * @return {Boolean}
       */
      isAdmin: function () {
        return currentUser.role === 'admin';
      },

      /**
       * Check if a user is an application admin
       *
       * @return {Boolean}
       */
      isApplicationAdmin: function (): boolean {
        return currentUser.role === 'application admin';
      },

      /**
       * Check if the role of a user is sufficient for a certain route
       *
       * @return {Boolean}
       */
      hasSufficientRole: function (role) {
        return currentUser.role === role;
      },

      /**
       * Get auth token
       */
      getToken: function () {
        return $cookies.get('sg');
      },

      getTokenPayload: function () {
        const token = this.getToken();
        if (!token) {
          return '';
        }
        const tokenSplit = token.split('.');
        return angular.fromJson($window.atob(tokenSplit[1]));
      },

      tokenExpired: function () {
        const token = this.getTokenPayload();
        if (!token) {
          return true;
        }
        return Date.now() > (token.exp * 1000);
      },

      /**
       * Get the role of a user in a project
       */
      getUserProjectRole: function (project) {
        const appRole = this.getCurrentUserRole();
        if (appRole.value === 'admin') {
          return 'manager';
        }

        const userCount = project.users.length;
        let _role = '';
        for (let i = 0; i < userCount; i++) {
          if (project.users[i].userId._id === currentUser._id) {
            _role = project.users[i].projectRole;
            break;
          }
        }
        return _role;
      }

    };
  }
})();
